<template>
    <div class="vumeter" v-resize-observer="resizeCanvas">
        <canvas ref="canvas"></canvas>
        <audio ref="audioMonitoringPlayer" style="display: none;" autoplay :muted="!audioMonitoring"></audio>
    </div>
</template>

<script>

const log = debug("Vumeter");

export default {
    props: {
        mediaStream: {
            type: MediaStream,
            required: true
        },
        maxDecibels: {
            type: Number,
            default: -5
        },
        minDecibels: {
            type: Number,
            default: -55
        },
        clipLevel: {
            type: Number,
            default: .95
        },
        clipDuration: {
            type: Number,
            default: 250
        },
        audioMonitoring: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            audioContext: undefined,
            analysers: [], // Array to hold analyser nodes for each channel
            dataArrays: [], // Array to hold data arrays for each analyser
            canvasCtx: undefined,
            animationFrame: undefined,
            meterLevels: 12, // Total number of squares per channel
            greenLevels: 6,
            yellowLevels: 4,
            redLevels: 2,
            height: undefined,
            width: undefined,
            clipTimeout: undefined, // Timeout for clip feature
            monitoringDestination: undefined,
            monitoringMediaStream: undefined
        }
    },

    mounted() {

        log('Vumeter mounted')

        this.initAudio()
        this.initCanvas()
        this.resizeCanvas()

        this.updateAudioMonitoring()

        this.draw()
    },

    beforeDestroy() {
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame)
        }
        if (this.audioContext) {
            this.audioContext.close()
        }
    },

    watch: {
        minDecibels() {
            this.updateAnalyserNodes()
        },
        maxDecibels() {
            this.updateAnalyserNodes()
        },
        audioMonitoring() {
            this.updateAudioMonitoring()
        },
        async mediaStream() {
            this.resetAudio()
            await this.initAudio()
            await this.updateAudioMonitoring()
        }
    },

    methods: {
        async initAudio() {
            try {
                // Vérifier si mediaStream existe
                if (!this.mediaStream) {
                    throw new Error('MediaStream not available');
                }

                if (this.audioContext) {
                    this.resetAudio()
                }

                this.audioContext = await new (window.AudioContext || window.webkitAudioContext)({
                    latencyHint: 'interactive',
                });

                // Create source from media stream
                const source = this.audioContext.createMediaStreamSource(this.mediaStream);

                // Get the number of channels from the audio track
                const tracks = this.mediaStream.getAudioTracks();
                if (!tracks.length) {
                    throw new Error('No audio tracks found');
                }

                const track = tracks[0];
                const channelCount = track.getSettings().channelCount || 2;

                // Create splitter to separate channels
                const splitter = this.audioContext.createChannelSplitter(channelCount);

                source.connect(splitter);

                // Create analyser nodes for each channel
                for (let i = 0; i < channelCount; i++) {
                    const analyser = new AnalyserNode(this.audioContext, {
                        fftSize: 2048,
                        smoothingTimeConstant: 0.75,
                        maxDecibels: this.maxDecibels,
                        minDecibels: this.minDecibels,
                    });

                    splitter.connect(analyser, i);
                    this.analysers.push(analyser);
                    this.dataArrays.push(new Float32Array(analyser.frequencyBinCount));
                }

                this.monitoringDestination = this.audioContext.createMediaStreamDestination();
                source.connect(this.monitoringDestination);

            } catch (error) {
                console.error('Error initializing audio:', error);
                // Émettre un événement d'erreur pour informer le composant parent
                this.$emit('audio-init-error', error);
            }
        },

        resetAudio() {
            if (this.audioContext) {
                this.audioContext.close();
                this.audioContext = undefined;
            }

            this.analysers = []
            this.dataArrays = []
        },

        initCanvas() {
            const canvas = this.$refs.canvas
            this.canvasCtx = canvas.getContext('2d')
        },

        resizeCanvas() {
            log('vumeter resized')
            const canvas = this.$refs.canvas
            const parent = canvas.parentNode
            canvas.width = parent.clientWidth
            canvas.height = parent.clientHeight
            this.width = canvas.width
            this.height = canvas.height
        },

        draw() {
            // Clear canvas
            this.canvasCtx.clearRect(0, 0, this.width, this.height)

            const transparencyHigh = 0.8
            const transparencyLow = 0.2

            const high = {
                green: `rgba(0,255,0,${transparencyHigh})`,
                yellow: `rgba(255,223,0,${transparencyHigh})`,
                red: `rgba(255,0,0,${transparencyHigh})`,
            }

            const low = {
                green: `rgba(0,255,0,${transparencyLow})`,
                yellow: `rgba(255,223,0,${transparencyLow})`,
                red: `rgba(255,0,0,${transparencyLow})`,
            }

            // Draw meter squares for each channel
            const gap = 2
            const squareHeight = parseInt((this.height - gap) / this.meterLevels)
            const squareWidth = parseInt((this.width - gap) / this.analysers.length)

            for (let channel = 0; channel < this.analysers.length; channel++) {
                const analyser = this.analysers[channel]
                const dataArray = this.dataArrays[channel]

                // Get current audio levels
                analyser.getFloatTimeDomainData(dataArray)

                // Calculate RMS value for each channel
                let rms = 0
                for (let i = 0; i < dataArray.length; i++) {
                    rms += dataArray[i] * dataArray[i]
                }
                rms = Math.sqrt(rms / dataArray.length)

                // Convert to dB and normalize
                const db = 20 * Math.log10(rms)
                const normalizedDb = Math.max(0, (db - analyser.minDecibels) /
                    (analyser.maxDecibels - analyser.minDecibels))


                // Check for clipping
                if (normalizedDb > this.clipLevel) {
                    this.triggerClip()
                }

                for (let i = 0; i < this.meterLevels; i++) {
                    const y = this.height - (i + 1) * squareHeight + gap

                    // Determine color based on level
                    let color
                    const activeLevels = normalizedDb * (this.meterLevels + 1)
                    if (this.clipTimeout) {
                        color = high.red
                    } else if (i < this.greenLevels) {
                        color = activeLevels >= i + 1 ? high.green : low.green
                    } else if (i < this.greenLevels + this.yellowLevels) {
                        color = activeLevels >= i + 1 ? high.yellow : low.yellow
                    } else {
                        color = activeLevels >= i + 1 ? high.red : low.red
                    }

                    this.canvasCtx.fillStyle = color
                    this.canvasCtx.fillRect(channel * (squareWidth + gap), y, squareWidth, squareHeight - gap)
                }
            }

            // Continue animation
            this.animationFrame = requestAnimationFrame(() => this.draw())
        },

        triggerClip() {
            if (this.clipTimeout) {
                clearTimeout(this.clipTimeout)
            }
            this.clipTimeout = setTimeout(() => {
                this.clipTimeout = null
            }, this.clipDuration)
        },

        updateAnalyserNodes() {
            this.analysers.forEach(analyser => {
                analyser.minDecibels = this.minDecibels
                analyser.maxDecibels = this.maxDecibels
            })
        },
        updateAudioMonitoring() {
            this.$refs.audioMonitoringPlayer.srcObject = this.audioMonitoring ? this.monitoringDestination?.stream || null : undefined
        }
    }
}
</script>

<style scoped>
.vumeter {
    display: block;
    overflow: hidden;

    position: absolute;

    width: 100%;
    height: 100%;


}
</style>